import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { validateSuiteToken } from '../utils/suite-auth';

/**
 * Entry route for Suite SSO: /auth/suite?suite_token=JWT&login_hint=email
 * Validates token with Suite API, creates app session, then redirects to /.
 */
const SuiteAuthPage = () => {
  const { loginFromSuiteToken } = useAuth();
  const [status, setStatus] = useState('validating'); // 'validating' | 'success' | 'invalid'
  const [message, setMessage] = useState('');

  useEffect(() => {
    let timeoutId;
    const params = new URLSearchParams(window.location.search);
    const suiteToken = params.get('suite_token');
    const loginHint = params.get('login_hint') || '';

    if (!suiteToken) {
      setMessage('No suite token. Redirecting to login.');
      setStatus('invalid');
      timeoutId = setTimeout(() => window.location.replace('/'), 1500);
      return () => clearTimeout(timeoutId);
    }

    let cancelled = false;
    (async () => {
      const result = await validateSuiteToken(suiteToken);
      if (cancelled) return;
      if (result.valid && result.payload) {
        await loginFromSuiteToken(suiteToken, loginHint);
        setStatus('success');
        setMessage('Signed in from Suite. Redirecting...');
        window.location.replace('/');
      } else {
        setStatus('invalid');
        setMessage('Invalid or expired token. Redirecting to login.');
        timeoutId = setTimeout(() => window.location.replace('/'), 2500);
      }
    })();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loginFromSuiteToken]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        {status === 'validating' && (
          <>
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#679632]"></div>
            <p className="mt-4 text-gray-600">Signing you in from Suite...</p>
          </>
        )}
        {(status === 'success' || status === 'invalid') && (
          <p className="text-gray-700">{message}</p>
        )}
      </div>
    </div>
  );
};

export default SuiteAuthPage;
